import { isGroupfolioEntityType } from '@upright/common/model/input/groupfolios/types';
import { Valence } from '@upright/common/model/types';
import { BranchingElbowConnector } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/connectors/branching-elbow-connector';
import {
  getAllBarBounds,
  getBoundsWithVerticalIntersect,
} from '@upright/ui/common/upright/core/impact-lens-profile/common/components/connectors/utils';
import { LensBoxMode, Side } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { valenceToSide } from '@upright/ui/common/upright/core/impact-lens-profile/common/utils';
import { useCarouselLayout, useImpactBarsLayout } from '@upright/ui/common/upright/core/impact-lens-profile/context';
import { useNetImpactLensContext } from '@upright/ui/common/upright/core/impact-lenses/net-impact/common/use-net-impact-lens-context';
import { max, min } from 'lodash';
import { connectionPointForDataAreaBoundsAtValence } from '../../../../sdg/common/layout/layout-utils';
import { getRelevantImpactCategories } from '../../../common/utils';

interface Props {
  valence: Valence.NEGATIVE | Valence.POSITIVE;
}

export function KeyTakeawayConnector(props: Props) {
  const { valence } = props;
  const ctx = useNetImpactLensContext();
  const takeaways = ctx?.data?.takeaways;
  const topImpactData = ctx?.data?.topImpactData;

  // Percentile labels render to the side of each bar's value, so the connector
  // needs to clear that extra horizontal space. Two cases force the suffix on:
  //   1. The user has `showCategoryPercentiles` turned on, OR
  //   2. The entity is a groupfolio (group / portfolio / fund) — the outside
  //      label always appends `(top X%)` for the highlighted top impacts on
  //      those pages, regardless of the toggle (see outside-label.tsx,
  //      `shouldShowHighlights` branch). Without this second case the lines
  //      overlap the suffix on fund pages.
  const showPercentiles = ctx?.state.showCategoryPercentiles ?? false;
  const isGroupfolio = isGroupfolioEntityType(ctx?.data?.entity?.type);
  const labelMargin = showPercentiles || isGroupfolio ? 30 : 0;

  const impacts = getRelevantImpactCategories(ctx?.data?.entity?.type, valence, takeaways, topImpactData);

  const baseLayout = useCarouselLayout();
  const layout = useImpactBarsLayout();

  if (baseLayout.boxMode !== LensBoxMode.SIDE) {
    return <></>;
  }

  const ba = baseLayout.boxesArea(valenceToSide(props.valence));

  if (!impacts?.length) {
    return <></>;
  }

  const endPoints = impacts.map(impact => {
    const refArea = layout.impactBarAndLabelArea(valenceToSide(valence), impact);
    const point = connectionPointForDataAreaBoundsAtValence(refArea, props.valence);
    return point;
  });

  const x2 = valence === Valence.POSITIVE ? ba.x1 : ba.x2;
  const y2 = ba.y1 + ba.height / 2;
  const startPoint = { x: x2, y: y2 };

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const side = startPoint.x > endPoints[0].x ? Side.RIGHT : Side.LEFT;
  const barBounds = getAllBarBounds(layout, side);

  const intersectingBounds = getBoundsWithVerticalIntersect(
    barBounds,
    endPoints.map(point => ({ y1: point.y, y2: startPoint.y })),
  );

  const midXValues = intersectingBounds.map(bounds => {
    const x1 = valence === Valence.NEGATIVE ? bounds.x1 : bounds.x2;
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const x2 = startPoint.x;
    return (x1 + x2) / 2;
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const branchX = valence === Valence.NEGATIVE ? min(midXValues)! : max(midXValues)!;

  return (
    <BranchingElbowConnector
      startPoint={startPoint}
      endPoints={endPoints}
      branchX={branchX}
      addedHorizontalMargin={labelMargin}
    />
  );
}
